// Saraya Design System Theme Configuration
// Modern, professional theme with Saraya brand colors
import { alpha } from '@mui/material/styles';

/**
 * SOURCE OF TRUTH: Brand Identity Tokens
 * Official Saraya Auto colors - DO NOT MODIFY
 */
const BRAND = {
  // Primary brand colors
  blue: '#1800ad',           // Primary brand blue
  orange: '#fd641f',         // Primary brand orange
  white: '#ffffff',          // Pure white

  // Extended blue palette (derived from #1800ad)
  blueLight: '#4d33d8',      // Lighter blue for hover states
  blueMedium: '#3320c4',     // Medium blue
  blueDark: '#12007a',       // Darker blue for emphasis
  blueUltraDark: '#0a0052',  // Ultra dark for dark mode backgrounds

  // Extended orange palette (derived from #fd641f)
  orangeLight: '#ff8a50',    // Lighter orange for hover states
  orangeMedium: '#e85a1a',   // Medium orange
  orangeDark: '#c44a15',     // Darker orange for emphasis

  // Neutral palette (professional grays)
  slate: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
    950: '#020617',
  },

  // Semantic colors
  success: '#10B981',
  error: '#EF4444',
  warning: '#F59E0B',
  info: '#3B82F6',
};

/**
 * Typography Configuration
 * Poppins Bold (700) for headings, Poppins Regular (400) for body text
 */
const FONTS = {
  primary: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  display: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

/**
 * Design tokens for consistent spacing and sizing
 */
const DESIGN_TOKENS = {
  borderRadius: {
    xs: 6,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 28,
    full: 9999,
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
};

/**
 * Get theme configuration based on mode (light/dark)
 */
const getThemeConfig = (mode) => ({
  palette: {
    mode,
    // Brand colors accessible globally
    brand: {
      primary: BRAND.orange,
      secondary: BRAND.blue,
      blue: BRAND.blue,
      blueLight: BRAND.blueLight,
      blueMedium: BRAND.blueMedium,
      blueDark: BRAND.blueDark,
      orange: BRAND.orange,
      orangeLight: BRAND.orangeLight,
      orangeMedium: BRAND.orangeMedium,
      orangeDark: BRAND.orangeDark,
      white: BRAND.white,
      gradients: mode === 'light'
        ? {
            main: BRAND.orange,
            mainLight: BRAND.orangeLight,
            dark: BRAND.blue,
          }
        : {
            main: `linear-gradient(135deg, ${BRAND.orangeDark} 0%, ${BRAND.orange} 100%)`,
            mainLight: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeLight} 100%)`,
            dark: `linear-gradient(135deg, ${BRAND.blueDark} 0%, ${BRAND.blue} 100%)`,
          },
    },
    slate: BRAND.slate,

    ...(mode === 'light'
      ? {
          // ============================================
          // LIGHT MODE - Clean & Professional
          // ============================================
          primary: {
            main: BRAND.orange,
            light: BRAND.orangeLight,
            dark: BRAND.orangeDark,
            contrastText: BRAND.white,
          },
          secondary: {
            main: BRAND.blue,
            light: BRAND.blueLight,
            dark: BRAND.blueDark,
            contrastText: BRAND.white,
          },
          background: {
            default: '#FAFBFC',           // Very subtle warm gray
            paper: BRAND.white,
            gradient: BRAND.white,
          },
          text: {
            primary: BRAND.slate[900],    // Near black for readability
            secondary: BRAND.slate[600],  // Medium gray for secondary text
            disabled: BRAND.slate[400],
          },
          divider: alpha(BRAND.slate[300], 0.5),
          common: {
            black: '#000000',
            white: BRAND.white,
          },
          success: {
            main: '#10B981',
            light: '#34D399',
            dark: '#059669',
            contrastText: BRAND.white,
          },
          error: {
            main: '#EF4444',
            light: '#F87171',
            dark: '#DC2626',
            contrastText: BRAND.white,
          },
          warning: {
            main: '#F59E0B',
            light: '#FBBF24',
            dark: '#D97706',
            contrastText: BRAND.slate[900],
          },
          info: {
            main: '#3B82F6',
            light: '#60A5FA',
            dark: '#2563EB',
            contrastText: BRAND.white,
          },
        }
      : {
          // ============================================
          // DARK MODE - Elegant & Immersive
          // ============================================
          primary: {
            main: BRAND.orange,
            light: BRAND.orangeLight,
            dark: BRAND.orangeDark,
            contrastText: BRAND.white,
          },
          secondary: {
            main: BRAND.blueLight,        // Lighter blue for visibility in dark mode
            light: '#6b5ce7',
            dark: BRAND.blue,
            contrastText: BRAND.white,
          },
          background: {
            default: '#0A0F1A',           // Deep dark blue-black
            paper: '#121827',             // Slightly lighter for cards
            gradient: `linear-gradient(180deg, #0A0F1A 0%, #0F1629 100%)`,
          },
          text: {
            primary: '#F8FAFC',           // Off-white for readability
            secondary: '#94A3B8',         // Soft gray for secondary
            disabled: '#475569',
          },
          divider: alpha(BRAND.slate[600], 0.3),
          common: {
            black: '#000000',
            white: BRAND.white,
          },
          success: {
            main: '#34D399',
            light: '#6EE7B7',
            dark: '#10B981',
            contrastText: BRAND.slate[900],
          },
          error: {
            main: '#F87171',
            light: '#FCA5A5',
            dark: '#EF4444',
            contrastText: BRAND.white,
          },
          warning: {
            main: '#FBBF24',
            light: '#FCD34D',
            dark: '#F59E0B',
            contrastText: BRAND.slate[900],
          },
          info: {
            main: '#60A5FA',
            light: '#93C5FD',
            dark: '#3B82F6',
            contrastText: BRAND.slate[900],
          },
        }),
  },

  // ============================================
  // TYPOGRAPHY
  // Poppins Bold (700) for headings
  // Poppins Regular (400) for body text
  // ============================================
  typography: {
    fontFamily: FONTS.primary,
    h1: {
      fontFamily: FONTS.display,
      fontSize: '3.25rem',
      fontWeight: 700,          // Poppins Bold
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: FONTS.display,
      fontSize: '2.5rem',
      fontWeight: 700,          // Poppins Bold
      lineHeight: 1.2,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontFamily: FONTS.display,
      fontSize: '2rem',
      fontWeight: 700,          // Poppins Bold
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: FONTS.display,
      fontSize: '1.625rem',
      fontWeight: 700,          // Poppins Bold
      lineHeight: 1.3,
    },
    h5: {
      fontFamily: FONTS.display,
      fontSize: '1.375rem',
      fontWeight: 700,          // Poppins Bold
      lineHeight: 1.35,
    },
    h6: {
      fontFamily: FONTS.display,
      fontSize: '1.125rem',
      fontWeight: 700,          // Poppins Bold
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '1.0625rem',
      fontWeight: 500,          // Poppins Medium
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.9375rem',
      fontWeight: 500,          // Poppins Medium
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,          // Poppins Regular
      lineHeight: 1.625,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,          // Poppins Regular
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,          // Poppins SemiBold for buttons
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },

  shape: {
    borderRadius: DESIGN_TOKENS.borderRadius.md,
  },

  // ============================================
  // SHADOWS - Modern, subtle shadows
  // ============================================
  shadows: mode === 'light'
    ? [
        'none',
        '0 1px 2px rgba(15, 23, 42, 0.04)',
        '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
        '0 4px 6px -1px rgba(15, 23, 42, 0.07), 0 2px 4px -1px rgba(15, 23, 42, 0.05)',
        '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -2px rgba(15, 23, 42, 0.04)',
        '0 20px 25px -5px rgba(15, 23, 42, 0.08), 0 10px 10px -5px rgba(15, 23, 42, 0.03)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.15)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.18)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.20)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.22)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.24)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.26)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.28)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.30)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.32)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.34)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.36)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.38)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.40)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.42)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.44)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.46)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.48)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.50)',
        '0 25px 50px -12px rgba(15, 23, 42, 0.52)',
      ]
    : [
        'none',
        '0 1px 2px rgba(0, 0, 0, 0.2)',
        '0 1px 3px rgba(0, 0, 0, 0.25), 0 1px 2px rgba(0, 0, 0, 0.2)',
        '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.25)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.35), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
        '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.55)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.60)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.65)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.70)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.75)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.80)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.85)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.90)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.95)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
        '0 25px 50px -12px rgba(0, 0, 0, 1)',
      ],

  // ============================================
  // COMPONENT OVERRIDES
  // ============================================
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        body: {
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: mode === 'light' ? BRAND.slate[100] : BRAND.slate[900],
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'light' ? BRAND.slate[300] : BRAND.slate[700],
            borderRadius: '5px',
            '&:hover': {
              background: mode === 'light' ? BRAND.slate[400] : BRAND.slate[600],
            },
          },
        },
        '::selection': {
          backgroundColor: alpha(BRAND.orange, 0.25),
          color: 'inherit',
        },
        '::-moz-selection': {
          backgroundColor: alpha(BRAND.orange, 0.25),
          color: 'inherit',
        },
        ':focus-visible': {
          outline: `2px solid ${BRAND.orange}`,
          outlineOffset: '2px',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.md,
          padding: '10px 24px',
          fontSize: '0.9375rem',
          fontWeight: 600,
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          boxShadow: mode === 'light'
            ? `0 4px 14px ${alpha(BRAND.orange, 0.25)}`
            : `0 4px 14px ${alpha(BRAND.orange, 0.35)}`,
          '&:hover': {
            boxShadow: mode === 'light'
              ? `0 6px 20px ${alpha(BRAND.orange, 0.35)}`
              : `0 6px 20px ${alpha(BRAND.orange, 0.45)}`,
          },
        },
        containedPrimary: {
          background: BRAND.orange,
          '&:hover': {
            background: BRAND.orangeDark,
          },
        },
        containedSecondary: {
          background: BRAND.blue,
          '&:hover': {
            background: BRAND.blueDark,
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
        outlinedPrimary: {
          borderColor: alpha(BRAND.orange, 0.5),
          '&:hover': {
            borderColor: BRAND.orange,
            background: alpha(BRAND.orange, 0.08),
          },
        },
        outlinedSecondary: {
          borderColor: alpha(BRAND.blue, 0.5),
          '&:hover': {
            borderColor: BRAND.blue,
            background: alpha(BRAND.blue, 0.08),
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.xl,
          backgroundColor: mode === 'light'
            ? BRAND.white
            : alpha(BRAND.slate[800], 0.6),
          border: `1px solid ${
            mode === 'light'
              ? alpha(BRAND.slate[200], 0.8)
              : alpha(BRAND.slate[700], 0.3)
          }`,
          boxShadow: mode === 'light'
            ? '0 4px 20px rgba(15, 23, 42, 0.06)'
            : '0 4px 20px rgba(0, 0, 0, 0.25)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: mode === 'light'
              ? `0 12px 32px ${alpha(BRAND.blue, 0.12)}`
              : `0 12px 32px rgba(0, 0, 0, 0.4)`,
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.lg,
          backgroundColor: mode === 'light'
            ? BRAND.white
            : alpha(BRAND.slate[800], 0.8),
          backgroundImage: 'none',
        },
        elevation0: {
          backgroundColor: mode === 'light'
            ? alpha(BRAND.slate[50], 0.8)
            : alpha(BRAND.slate[900], 0.5),
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: DESIGN_TOKENS.borderRadius.md,
            backgroundColor: mode === 'light'
              ? BRAND.white
              : alpha(BRAND.slate[800], 0.6),
            transition: 'all 0.25s ease',
            '& fieldset': {
              borderColor: mode === 'light'
                ? BRAND.slate[300]
                : BRAND.slate[700],
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: mode === 'light'
                ? BRAND.slate[400]
                : BRAND.slate[600],
            },
            '&.Mui-focused': {
              backgroundColor: mode === 'light'
                ? BRAND.white
                : alpha(BRAND.slate[800], 0.8),
              '& fieldset': {
                borderColor: BRAND.orange,
                borderWidth: '2px',
              },
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: BRAND.orange,
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.sm,
          fontWeight: 600,
          fontSize: '0.8125rem',
        },
        filled: {
          backgroundColor: alpha(BRAND.orange, 0.1),
          color: mode === 'light' ? BRAND.orangeDark : BRAND.orangeLight,
          '&:hover': {
            backgroundColor: alpha(BRAND.orange, 0.2),
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: alpha(BRAND.orange, 0.3),
          backgroundColor: mode === 'light'
            ? alpha(BRAND.orange, 0.04)
            : alpha(BRAND.orange, 0.08),
        },
        colorPrimary: {
          backgroundColor: BRAND.orange,
          color: BRAND.white,
          '&:hover': {
            backgroundColor: BRAND.orangeDark,
          },
        },
        colorSecondary: {
          backgroundColor: BRAND.blue,
          color: BRAND.white,
          '&:hover': {
            backgroundColor: BRAND.blueDark,
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.md,
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            backgroundColor: alpha(BRAND.orange, 0.1),
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.md,
        },
        standardSuccess: {
          backgroundColor: alpha('#10B981', mode === 'light' ? 0.1 : 0.2),
          color: mode === 'light' ? '#059669' : '#34D399',
        },
        standardError: {
          backgroundColor: alpha('#EF4444', mode === 'light' ? 0.1 : 0.2),
          color: mode === 'light' ? '#DC2626' : '#F87171',
        },
        standardWarning: {
          backgroundColor: alpha('#F59E0B', mode === 'light' ? 0.1 : 0.2),
          color: mode === 'light' ? '#D97706' : '#FBBF24',
        },
        standardInfo: {
          backgroundColor: alpha('#3B82F6', mode === 'light' ? 0.1 : 0.2),
          color: mode === 'light' ? '#2563EB' : '#60A5FA',
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.md,
          backgroundColor: mode === 'light'
            ? alpha(BRAND.slate[400], 0.15)
            : alpha(BRAND.slate[600], 0.25),
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: mode === 'light'
            ? alpha(BRAND.slate[300], 0.5)
            : alpha(BRAND.slate[700], 0.5),
        },
      },
    },

    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            borderRadius: DESIGN_TOKENS.borderRadius.sm,
            fontWeight: 600,
            '&.Mui-selected': {
              backgroundColor: BRAND.orange,
              color: BRAND.white,
              '&:hover': {
                backgroundColor: BRAND.orangeDark,
              },
            },
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'light'
            ? BRAND.slate[800]
            : BRAND.slate[700],
          color: BRAND.white,
          borderRadius: DESIGN_TOKENS.borderRadius.sm,
          fontSize: '0.8125rem',
          fontWeight: 500,
          padding: '8px 12px',
        },
        arrow: {
          color: mode === 'light'
            ? BRAND.slate[800]
            : BRAND.slate[700],
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: DESIGN_TOKENS.borderRadius.lg,
          boxShadow: mode === 'light'
            ? '0 10px 40px rgba(15, 23, 42, 0.12)'
            : '0 10px 40px rgba(0, 0, 0, 0.4)',
          border: `1px solid ${
            mode === 'light'
              ? alpha(BRAND.slate[200], 0.8)
              : alpha(BRAND.slate[700], 0.5)
          }`,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.sm,
          margin: '2px 6px',
          padding: '10px 14px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(BRAND.orange, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(BRAND.orange, 0.12),
            '&:hover': {
              backgroundColor: alpha(BRAND.orange, 0.16),
            },
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: mode === 'light'
            ? BRAND.white
            : BRAND.slate[900],
          borderRight: `1px solid ${
            mode === 'light'
              ? alpha(BRAND.slate[200], 0.8)
              : alpha(BRAND.slate[800], 0.8)
          }`,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: DESIGN_TOKENS.borderRadius.xl,
          boxShadow: mode === 'light'
            ? '0 25px 50px -12px rgba(15, 23, 42, 0.25)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: mode === 'light'
            ? alpha(BRAND.slate[200], 0.8)
            : alpha(BRAND.slate[700], 0.5),
        },
        head: {
          fontWeight: 700,
          backgroundColor: mode === 'light'
            ? BRAND.slate[50]
            : alpha(BRAND.slate[800], 0.5),
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: mode === 'light'
              ? alpha(BRAND.orange, 0.04)
              : alpha(BRAND.orange, 0.08),
          },
        },
      },
    },

    MuiSlider: {
      styleOverrides: {
        root: {
          color: BRAND.orange,
          '& .MuiSlider-thumb': {
            backgroundColor: BRAND.white,
            border: `2px solid ${BRAND.orange}`,
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 0 8px ${alpha(BRAND.orange, 0.16)}`,
            },
          },
          '& .MuiSlider-track': {
            backgroundColor: BRAND.orange,
          },
          '& .MuiSlider-rail': {
            backgroundColor: mode === 'light'
              ? BRAND.slate[300]
              : BRAND.slate[600],
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: BRAND.orange,
            '& + .MuiSwitch-track': {
              backgroundColor: BRAND.orange,
            },
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: DESIGN_TOKENS.borderRadius.md,
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: DESIGN_TOKENS.borderRadius.lg,
          boxShadow: mode === 'light'
            ? '0 10px 40px rgba(15, 23, 42, 0.15)'
            : '0 10px 40px rgba(0, 0, 0, 0.4)',
        },
      },
    },
  },
});

export default getThemeConfig;
